import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {message} from 'antd';
import {AxiosError, AxiosResponse} from 'axios';
import chargerCategoryServices from './chargerCategoryServices';
import type {
  ADD_CHARGER_CATEGORY,
  UPDATE_CHARGER_CATEGORY,
  CHARGER_CATEGORY,
} from './chargerCategory.type';
import type FILTERS from '../../../../common/filters.type';

const initialState = {
  isLoading: false,
  chargerCategories: {
    page_size: 10,
    page_number: 1,
    sort_by: 'name',
    ascending: true,
    search: '',
    totalDataCount: 0,
    is_active: undefined as undefined | boolean,
    pageData: [] as CHARGER_CATEGORY[],
  },
};

export const chargerCategoryList = createAsyncThunk(
  'chargerCategoryList',
  async (payload: FILTERS, thunkAPI) => {
    try {
      const data: AxiosResponse = await chargerCategoryServices.chargerCategoriesList(payload);

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const addChargerCategory = createAsyncThunk(
  'addChargerCategory',
  async (values: ADD_CHARGER_CATEGORY, thunkAPI) => {
    try {
      const data: AxiosResponse = await chargerCategoryServices.addChargerCategory(values);

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);
export const updateChargerCategory = createAsyncThunk(
  'updateChargerCategory',
  async (
    values: {
      payload: UPDATE_CHARGER_CATEGORY;
      category_id: number;
    },
    thunkAPI,
  ) => {
    try {
      const data: AxiosResponse = await chargerCategoryServices.updateChargerCategory({
        payload: values.payload,
        category_id: values.category_id,
      });

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const chargerCategorySlice = createSlice({
  name: 'chargerCategories',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    pageNumberChange(state, action: PayloadAction<number>) {
      state.chargerCategories.page_number = action.payload;
    },
    pageSizeChange(state, action: PayloadAction<number>) {
      state.chargerCategories.page_number = 1;
      state.chargerCategories.page_size = action.payload;
    },
    searchBy(state, action: PayloadAction<string>) {
      state.chargerCategories.page_number = 1;
      state.chargerCategories.search = action.payload;
    },
    sortBy(state, action: PayloadAction<{ascending: boolean; sortBy: string}>) {
      state.chargerCategories.ascending = action.payload.ascending;
      state.chargerCategories.sort_by = action.payload.sortBy;
    },
    chargerCategoryStatusFilter(state, action: PayloadAction<boolean | undefined>) {
      state.chargerCategories.page_number = 1;
      state.chargerCategories.is_active = action.payload;
    },
    clearChargerCategoryFilters(state) {
      state.chargerCategories.page_size = 10;
      state.chargerCategories.page_number = 1;
      state.chargerCategories.search = '';
      state.chargerCategories.sort_by = 'name';
      state.chargerCategories.is_active = undefined;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(chargerCategoryList.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(chargerCategoryList.fulfilled, (state, action) => {
      state.isLoading = false;
      state.chargerCategories = action.payload.data;
    });
    builder.addCase(chargerCategoryList.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });

    builder.addCase(addChargerCategory.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(addChargerCategory.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`Charger Category Added Successfully`);
    });
    builder.addCase(addChargerCategory.rejected, (state, action) => {
      state.isLoading = false;

      message.error(`${action.payload}`);
    });

    builder.addCase(updateChargerCategory.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateChargerCategory.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`Charger Category updated Successfully`);
    });
    builder.addCase(updateChargerCategory.rejected, (state, action) => {
      state.isLoading = false;

      message.error(`${action.payload}`);
    });
  },
});
export const {
  pageNumberChange,
  pageSizeChange,
  searchBy,
  sortBy,
  clearChargerCategoryFilters,
  chargerCategoryStatusFilter,
} = chargerCategorySlice.actions;

export default chargerCategorySlice.reducer;
